// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\samples\index.js

import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { 
  InfoCircleOutlined, PlusOutlined, UploadOutlined, DownloadOutlined 
} from "@ant-design/icons";
import { Button, Card, Col, Row, Table, Pagination, Modal, Space, Upload, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import Filter from "../../components/filter/Filter";
import { toast } from "react-toastify";
import { deleteSamples, getSamples, importSamples } from "../../redux/slices/samples/samplesThunk";
import { columns } from "./columns";
import ImportComponent from "../../components/logModal";
import axiosInstance from "../../services/axiosServicesVisit";
import * as Papa from "papaparse";
import dayjs from "dayjs";

const SamplesPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [search, setSearch] = useState({
    searchTerm: "",
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
  });

  const [showLog, setShowLog] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemId, setItemId] = useState(null);
  const [exporting, setExporting] = useState(false); // ⚡ État de chargement pour l'export

  const samples_data = useSelector((state) => state.samples?.samples?.data);
  const samples_loading = useSelector((state) => state.samples?.samples?.loading);
  const totalItems = useSelector((state) => state.samples.samples?.total);
  const loadingImportation = useSelector((state) => state.samples?.importSamples.loading);

  const handlePagination = (page, pageSize) => {
    setPagination({ pageSize: pageSize, currentPage: page });
  };

  const navigateTo = (path) => {
    history.push(path);
  };

  const showModal = (id) => {
    setIsModalOpen(true);
    setItemId(id);
  };

  const handleDelete = (id) => {
    dispatch(deleteSamples(id)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        dispatch(getSamples({
          page: pagination.currentPage,
          limit: 10,
          filter: { search: search.searchTerm }
        }));
        toast.success("Échantillon supprimé avec succès");
        navigateTo("/samples");
      } else {
        toast.error("Erreur pendant la suppression !");
      }
    }).catch(error => {
      console.error(error);
      toast.error("Une erreur est survenue !");
    });
    setIsModalOpen(false);
  };

  useEffect(() => {
    dispatch(getSamples({
      page: pagination.currentPage, 
      limit: 10, 
      filter: { search: search.searchTerm }
    }));
  }, [dispatch, search, pagination]);

  const handleSearchedValue = (value, key) => {
    if (search[key] !== value) {
      setSearch((prevSearch) => ({
        ...prevSearch,
        [key]: value,
      }));
      if (key === "searchTerm")
        setPagination((prevPagination) => ({
          ...prevPagination,
          currentPage: 1,
        }));
    }
  };

  const handleFileUpload = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    dispatch(importSamples(formData))
      .then(response => {
        if (response.meta.requestStatus === "fulfilled") {
          toast.success('Fichier importé avec succès');
          dispatch(getSamples({
            page: pagination.currentPage, 
            limit: 10, 
            filter: { search: search.searchTerm }
          }));
        }
      })
      .catch(error => {
        toast.error("Erreur lors de l'importation du fichier", error);
      });
  };

  const handleShowLog = () => {
    setShowLog(!showLog);
  };

  // =========================================================================
  // ⚡ FONCTION D'EXPORT CSV AVEC PRISE EN CHARGE DU LABEL ⚡
  // =========================================================================
  const handleExportCSV = async () => {
    try {
      setExporting(true);

      // 1. Récupération de TOUS les échantillons (limit = 10000) en respectant la recherche courante
      const response = await axiosInstance.get('/v1/samples', {
        params: {
          page: 1,
          limit: 10000,
          search: search.searchTerm || undefined,
        }
      });

      const list = response.data?.data || response.data || [];

      if (!list || list.length === 0) {
        message.warning("Aucun échantillon à exporter.");
        return;
      }

      // 2. Formatage des colonnes pour le CSV
      const formattedData = list.map((item) => ({
        "Code": item.sampleCode || "",
        "Nom d'échantillon": item.name || "",
        "Label (Abréviation)": item.label || "",
      }));

      // 3. Génération du CSV avec Papaparse (délimiteur point-virgule pour Excel français)
      const csv = Papa.unparse(formattedData, {
        header: true,
        delimiter: ";",
      });

      // 4. Téléchargement du fichier avec BOM UTF-8 pour supporter les accents
      const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `echantillons_${dayjs().format("YYYY-MM-DD")}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success(`${formattedData.length} échantillon(s) exporté(s) avec succès.`);
    } catch (error) {
      console.error("Erreur export échantillons :", error);
      message.error("Erreur lors de l'exportation des échantillons.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title="Liste des échantillons"
            extra={
              <Space>
                <Button
                  shape="circle"
                  icon={
                    loadingImportation ? (
                      <InfoCircleOutlined style={{ color: 'orange' }} />
                    ) : (
                      <InfoCircleOutlined />
                    )
                  }
                  onClick={handleShowLog}
                />

                {/* ⚡ BOUTON EXPORTER CSV ⚡ */}
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExportCSV}
                  loading={exporting}
                >
                  Exporter CSV
                </Button>

                <Upload
                  accept=".csv"
                  customRequest={({ file, onSuccess }) => {
                    handleFileUpload(file);
                    onSuccess();
                  }}
                  showUploadList={false}
                  disabled={loadingImportation}
                >
                  <Button
                    type="primary"
                    icon={<UploadOutlined />}
                    loading={loadingImportation}
                  >
                    Importer
                  </Button>
                </Upload>

                <Button
                  type="primary"
                  onClick={() => history.push("/nouveau/échantillon")}
                >
                  <PlusOutlined />
                  Ajouter un échantillon
                </Button>
              </Space>
            }
          />
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title={
              <Filter
                searchPlaceholder="Rechercher par nom, code ou label"
                onSearchValue={handleSearchedValue}
              />
            }
          >
            <Table
              columns={columns(history, showModal)}
              dataSource={samples_data}
              className="ant-border-space"
              rowClassName={(record, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
              pagination={false}
              rowKey="id"
              loading={samples_loading}
              scroll={{ x: 800 }}
            />
          </Card>
          <Pagination
            current={pagination.currentPage}
            total={totalItems}
            pageSize={pagination.pageSize}
            onChange={handlePagination}
          />
        </Col>

        <Modal
          title="Supprimer un Échantillon"
          visible={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          footer={[
            <Button key="back" onClick={() => setIsModalOpen(false)}>
              Annuler
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={() => handleDelete(itemId)}
            >
              Supprimer
            </Button>,
          ]}
        >
          <p>Êtes-vous sûr de vouloir supprimer cet échantillon ?</p>
        </Modal>
      </Row>
      <ImportComponent visible={showLog} onCancel={handleShowLog}></ImportComponent>
    </div>
  );
};

export default SamplesPage;